"use client";

import { env } from "~/app/env";
import { useState } from "react";
import SuperJSON from "superjson";
import { type AppRouter } from "@introw/api/types";
import { createTRPCReact } from "@trpc/react-query";
import { type QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpLink, loggerLink } from "@trpc/client";
import { createQueryClient } from "./query-client";

let clientQueryClientSingleton: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === "undefined") {
    // Server: always make a new query client
    return createQueryClient();
  } else {
    // Browser: use singleton pattern to keep the same query client
    return (clientQueryClientSingleton ??= createQueryClient());
  }
};

const getBaseUrl = () => {
  if (typeof window !== "undefined") return window.location.origin;
  return `http://localhost:${env.PORT ?? 3000}`;
};

const links = [
  loggerLink({
    enabled: () => env.NODE_ENV === "development",
  }),
  httpLink({
    transformer: SuperJSON,
    url: getBaseUrl() + "/api/trpc",
    headers() {
      const headers = new Headers();
      if (typeof window === "undefined") return new Headers();
      headers.set("x-trpc-source", "nextjs-react");
      const querySearch = new URLSearchParams(window.location.search);
      const token = querySearch.get("token");
      if (!!token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
];

// Use this to query awaitable
export const nativeClient = createTRPCClient<AppRouter>({
  links: links,
});

export const api = createTRPCReact<AppRouter>({});

export function TRPCReactProvider(props: { children: React.ReactNode }) {
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() => api.createClient({ links: links }));

  return (
    <QueryClientProvider client={queryClient}>
      <api.Provider client={trpcClient} queryClient={queryClient}>
        {props.children}
      </api.Provider>
    </QueryClientProvider>
  );
}
